import React, { useState, useCallback, useEffect, memo, useRef } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import CustomToolbar from "./Toolbar/CustomToolbar";
import ContextMenu from "./ContextMenu/ContextMenu";
import ImageEditor from "./ImageEditor/ImageEditor";
import ImageFloatingToolbar from "./ImageToolbar/ImageFloatingToolbar";
import "./HTMLEditor.css";
import { useEditorState } from "./hooks/useEditorState";
import { useContextMenu } from "./hooks/useContextMenu";
import { useImageManipulation } from "./hooks/useImageManipulation";
import useImageUpload from "./hooks/useImageUpload";

// Register custom formats for Quill
const Font = Quill.import("formats/font");
Font.whitelist = [
  "Arial",
  "Georgia",
  "Times New Roman",
  "Courier New",
  "Verdana",
  "Helvetica",
  "Comic Sans MS",
  "Impact",
  "Trebuchet MS",
  "Palatino",
];
Quill.register(Font, true);

const Size = Quill.import("formats/size");
Size.whitelist = [
  "10px",
  "12px",
  "14px",
  "16px",
  "18px",
  "20px",
  "24px",
  "28px",
  "32px",
  "36px",
  "48px",
  "64px",
];
Quill.register(Size, true);

// Register custom Image format to preserve alignment classes
const ImageFormat = Quill.import("formats/image");
class CustomImage extends ImageFormat {
  static formats(domNode) {
    const formats = super.formats ? super.formats(domNode) : {};
    // Preserve alignment classes
    const classList = domNode.classList;
    if (classList.contains("align-left")) formats["data-align"] = "left";
    if (classList.contains("align-center")) formats["data-align"] = "center";
    if (classList.contains("align-right")) formats["data-align"] = "right";
    return formats;
  }

  format(name, value) {
    if (name === "data-align" && value) {
      // Remove all alignment classes first
      this.domNode.classList.remove(
        "align-left",
        "align-center",
        "align-right"
      );
      // Add the new alignment class
      this.domNode.classList.add(`align-${value}`);
      this.domNode.setAttribute("data-align", value);
    } else {
      super.format(name, value);
    }
  }
}
Quill.register(CustomImage, true);

const HTMLEditor = memo(
  ({
    initialContent = "",
    value, // Support both value and initialContent props
    onChange,
    height = "500px",
    placeholder = "Start typing...",
    readOnly = false,
    theme = "light",
    className = "",
    onSave,
    enableImageUpload = true,
    enableContextMenu = true,
  }) => {
    // Use value prop if provided, otherwise use initialContent
    const contentToUse = value !== undefined ? value : initialContent;

    const { content, setContent, quillRef, getEditor, undo, redo } =
      useEditorState(contentToUse);

    const { contextMenu, selectedElement, showContextMenu, hideContextMenu } =
      useContextMenu();
    const { selectedImage, selectImage, imageProperties } =
      useImageManipulation();
    const { uploadImage } = useImageUpload();
    const [imageEditorOpen, setImageEditorOpen] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const isUploadingRef = useRef(false);

    // Floating toolbar state
    const [floatingToolbar, setFloatingToolbar] = useState(null);
    const [activeImage, setActiveImage] = useState(null);

    // Source view state
    const [showSource, setShowSource] = useState(false);

    // Full screen state
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [sourceHTML, setSourceHTML] = useState("");

    // Drag state for internal image drag
    const [draggedImage, setDraggedImage] = useState(null);

    // Handle content change
    const handleChange = useCallback(
      (value) => {
        setContent(value);
        if (onChange) {
          onChange(value);
        }
      },
      [setContent, onChange]
    );

    // Handle image click to show floating toolbar
    const handleImageClick = useCallback((event) => {
      const target = event.target;
      if (target.tagName === "IMG") {
        event.stopPropagation();

        const rect = target.getBoundingClientRect();

        // Calculate scroll offset
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

        // Position toolbar above the image
        setFloatingToolbar({
          top: rect.top + scrollTop,
          left: rect.left + rect.width / 2,
        });
        setActiveImage(target);
      }
    }, []);

    // Handle image drag start
    const handleImageDragStart = useCallback((event) => {
      const target = event.target;
      if (target.tagName === "IMG") {
        // Store the dragged image element with all its properties
        const imageData = {
          src: target.src,
          width: target.style.width || target.getAttribute("width"),
          height: target.style.height || target.getAttribute("height"),
          className: target.className,
          dataAlign: target.getAttribute("data-align"),
          dataSize: target.getAttribute("data-size"),
          style: target.getAttribute("style"),
        };

        setDraggedImage({
          element: target,
          data: imageData,
        });

        // Set data for external drops (optional)
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/html", target.outerHTML);
      }
    }, []);

    // Handle image drag end
    const handleImageDragEnd = useCallback(() => {
      setDraggedImage(null);
    }, []);

    // Handle image alignment from floating toolbar
    const handleImageAlign = useCallback(
      (alignment) => {
        if (activeImage) {
          const editor = getEditor();
          if (editor) {
            // Find the image's position in Quill
            const blot = Quill.find(activeImage);
            if (blot) {
              const index = editor.getIndex(blot);
              // Apply the format to the image using Quill's format method
              editor.formatText(index, 1, "data-align", alignment);

              // Also update DOM directly for immediate visual feedback
              activeImage.classList.remove(
                "align-left",
                "align-center",
                "align-right"
              );
              if (alignment) {
                activeImage.classList.add(`align-${alignment}`);
                activeImage.setAttribute("data-align", alignment);
              }
            }
          }
        }
        setFloatingToolbar(null);
        setActiveImage(null);
      },
      [activeImage, getEditor]
    );

    // Handle image resize from floating toolbar
    const handleImageResize = useCallback(
      (size) => {
        if (activeImage) {
          // Set width based on percentage
          activeImage.style.width = size;
          activeImage.style.height = "auto";
          activeImage.setAttribute("data-size", size);
        }
        setFloatingToolbar(null);
        setActiveImage(null);
      },
      [activeImage]
    );

    // Handle image delete from floating toolbar
    const handleImageDelete = useCallback(() => {
      if (activeImage) {
        activeImage.remove();
      }
      setFloatingToolbar(null);
      setActiveImage(null);
    }, [activeImage]);

    // Handle image edit from floating toolbar
    const handleImageEdit = useCallback(() => {
      if (activeImage) {
        // Get current display dimensions
        const currentWidth = activeImage.offsetWidth;
        const currentHeight = activeImage.offsetHeight;

        // Pass both the element and current dimensions
        selectImage(activeImage, {
          width: currentWidth,
          height: currentHeight,
        });
        setImageEditorOpen(true);
        setFloatingToolbar(null);
        setActiveImage(null);
      }
    }, [activeImage, selectImage]);

    // Close floating toolbar
    const closeFloatingToolbar = useCallback(() => {
      setFloatingToolbar(null);
      setActiveImage(null);
    }, []);

    // Toggle source view
    const handleToggleSource = useCallback(() => {
      if (!showSource) {
        // Switching to source view - save current HTML
        setSourceHTML(content);
      } else {
        // Switching back to editor - update content from source
        setContent(sourceHTML);
        if (onChange) {
          onChange(sourceHTML);
        }
      }
      setShowSource(!showSource);
    }, [showSource, content, sourceHTML, setContent, onChange]);

    // Handle source HTML change
    const handleSourceChange = useCallback((e) => {
      setSourceHTML(e.target.value);
    }, []);

    // Toggle full screen
    const handleToggleFullScreen = useCallback(() => {
      setIsFullScreen(!isFullScreen);
    }, [isFullScreen]);

    // Context menu items for images
    const imageContextMenuItems = [
      {
        id: "flip-h",
        label: "Flip Horizontal",
        icon: "↔️",
        action: (element) => {
          if (element) {
            const current = element.style.transform || "";
            element.style.transform = current.includes("scaleX(-1)")
              ? current.replace("scaleX(-1)", "scaleX(1)")
              : current + " scaleX(-1)";
          }
        },
      },
      {
        id: "flip-v",
        label: "Flip Vertical",
        icon: "↕️",
        action: (element) => {
          if (element) {
            const current = element.style.transform || "";
            element.style.transform = current.includes("scaleY(-1)")
              ? current.replace("scaleY(-1)", "scaleY(1)")
              : current + " scaleY(-1)";
          }
        },
      },
      {
        id: "rotate",
        label: "Rotate 90°",
        icon: "↻",
        action: (element) => {
          if (element) {
            const current = element.style.transform || "";
            const match = current.match(/rotate\(([0-9]+)deg\)/);
            const currentRotation = match ? parseInt(match[1]) : 0;
            const newRotation = (currentRotation + 90) % 360;
            element.style.transform =
              current.replace(/rotate\([0-9]+deg\)/, "") +
              ` rotate(${newRotation}deg)`;
          }
        },
      },
      { divider: true },
      {
        id: "edit-image",
        label: "Edit Image",
        icon: "✏️",
        action: (element) => {
          const currentWidth = element.offsetWidth;
          const currentHeight = element.offsetHeight;
          selectImage(element, { width: currentWidth, height: currentHeight });
          setImageEditorOpen(true);
        },
      },
      {
        id: "delete",
        label: "Delete",
        icon: "🗑️",
        action: (element) => {
          if (element) {
            element.remove();
          }
        },
      },
    ];

    // Handle right-click - only show menu for images
    const handleContextMenu = useCallback(
      (event) => {
        if (!enableContextMenu) return;

        const target = event.target;
        const isImage = target.tagName === "IMG";

        // Only show context menu for images
        if (!isImage) return;

        event.preventDefault();
        showContextMenu(event, target);
      },
      [enableContextMenu, showContextMenu]
    );

    // Handle drag and drop
    const handleDragOver = useCallback((event) => {
      event.preventDefault();
      setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback(() => {
      setIsDragging(false);
    }, []);

    const handleDrop = useCallback(
      async (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);

        const editor = getEditor();
        if (!editor) return;

        // Check if this is an internal image drag (move operation)
        if (draggedImage) {
          // Get the original image position
          const originalBlot = Quill.find(draggedImage.element);
          if (!originalBlot) {
            setDraggedImage(null);
            return;
          }

          const originalIndex = editor.getIndex(originalBlot);

          // Calculate drop position
          let dropIndex = 0;
          try {
            let range = null;
            if (document.caretRangeFromPoint) {
              range = document.caretRangeFromPoint(
                event.clientX,
                event.clientY
              );
            } else if (document.caretPositionFromPoint) {
              const position = document.caretPositionFromPoint(
                event.clientX,
                event.clientY
              );
              if (position) {
                range = document.createRange();
                range.setStart(position.offsetNode, position.offset);
              }
            }

            if (range) {
              const blot = Quill.find(range.startContainer, true);
              if (blot) {
                dropIndex = editor.getIndex(blot);
                if (range.startOffset > 0 && blot.length) {
                  dropIndex += Math.min(range.startOffset, blot.length());
                }
              }
            }
          } catch (e) {
            const selection = editor.getSelection();
            dropIndex = selection ? selection.index : editor.getLength() - 1;
          }

          // Don't do anything if dropping at the same position
          if (dropIndex === originalIndex || dropIndex === originalIndex + 1) {
            setDraggedImage(null);
            return;
          }

          // Remove the original image first
          editor.deleteText(originalIndex, 1);

          // Adjust drop index if it was after the original position
          if (dropIndex > originalIndex) {
            dropIndex -= 1;
          }

          // Insert the image at new position with all properties preserved
          editor.insertEmbed(dropIndex, "image", draggedImage.data.src);

          // Reapply all the styling and attributes
          setTimeout(() => {
            const newImg =
              editor.root.querySelectorAll("img")[dropIndex] ||
              Array.from(editor.root.querySelectorAll("img")).find(
                (img) => img.src === draggedImage.data.src
              );

            if (newImg) {
              // Restore all properties
              if (draggedImage.data.width)
                newImg.style.width = draggedImage.data.width;
              if (draggedImage.data.height)
                newImg.style.height = draggedImage.data.height;
              if (draggedImage.data.className)
                newImg.className = draggedImage.data.className;
              if (draggedImage.data.dataAlign)
                newImg.setAttribute("data-align", draggedImage.data.dataAlign);
              if (draggedImage.data.dataSize)
                newImg.setAttribute("data-size", draggedImage.data.dataSize);
              if (draggedImage.data.style)
                newImg.setAttribute("style", draggedImage.data.style);
            }
          }, 0);

          editor.setSelection(dropIndex + 1);
          setDraggedImage(null);
          return;
        }

        // Calculate exact drop position using browser's caret position
        let dropIndex = 0;

        try {
          // Use browser's native caret position detection
          let range = null;

          // Try modern API first
          if (document.caretRangeFromPoint) {
            range = document.caretRangeFromPoint(event.clientX, event.clientY);
          } else if (document.caretPositionFromPoint) {
            // Firefox alternative
            const position = document.caretPositionFromPoint(
              event.clientX,
              event.clientY
            );
            if (position) {
              range = document.createRange();
              range.setStart(position.offsetNode, position.offset);
            }
          }

          if (range) {
            // Get the blot (Quill element) at this position
            const blot = Quill.find(range.startContainer, true);
            if (blot) {
              dropIndex = editor.getIndex(blot);

              // Add offset within the blot
              if (range.startOffset > 0 && blot.length) {
                dropIndex += Math.min(range.startOffset, blot.length());
              }
            }
          } else {
            // Last fallback: use current selection
            const selection = editor.getSelection();
            dropIndex = selection ? selection.index : editor.getLength() - 1;
          }
        } catch (e) {
          // Fallback: use current cursor position
          const selection = editor.getSelection();
          dropIndex = selection ? selection.index : editor.getLength() - 1;
          console.log("Using fallback drop position:", dropIndex);
        }

        const files = Array.from(event.dataTransfer.files);
        const imageFiles = files.filter((file) =>
          file.type.startsWith("image/")
        );

        // Priority 1: Handle actual image files
        if (imageFiles.length > 0 && enableImageUpload) {
          setUploadingImage(true);

          for (const file of imageFiles) {
            try {
              // Upload image to server
              const imageUrl = await uploadImage(file);

              if (imageUrl) {
                // Insert at drop position
                editor.insertEmbed(dropIndex, "image", imageUrl);
                editor.setSelection(dropIndex + 1);
                dropIndex += 1; // Increment for next image
              } else {
                console.error("Failed to upload image");
                alert("Failed to upload image. Please try again.");
              }
            } catch (error) {
              console.error("Error uploading image:", error);
              alert("Error uploading image. Please try again.");
            }
          }

          setUploadingImage(false);
          return; // Exit after handling files
        }

        // Priority 2: Handle HTML content (e.g., dragging image from browser)
        const html = event.dataTransfer.getData("text/html");
        if (html) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, "text/html");
          const img = doc.querySelector("img");

          if (img && img.src) {
            // Insert image at drop position
            editor.insertEmbed(dropIndex, "image", img.src);
            editor.setSelection(dropIndex + 1);
            return; // Exit after handling image
          }
        }

        // Priority 3: Handle plain text URLs that might be images
        const text = event.dataTransfer.getData("text/plain");
        if (text) {
          // Check if it's an image URL
          const imageUrlPattern =
            /^https?:\/\/.+\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)(\?.*)?$/i;

          if (imageUrlPattern.test(text.trim())) {
            // It's an image URL - insert as image at drop position
            editor.insertEmbed(dropIndex, "image", text.trim());
            editor.setSelection(dropIndex + 1);
          } else {
            // Regular text - insert at drop position
            editor.insertText(dropIndex, text);
            editor.setSelection(dropIndex + text.length);
          }
        }
      },
      [enableImageUpload, getEditor, uploadImage, draggedImage]
    );

    // Handle image update from editor (with server upload)
    const handleImageUpdate = useCallback(
      async (updatedImage) => {
        // Prevent duplicate uploads
        if (!selectedImage || isUploadingRef.current) {
          console.log("Upload already in progress or no image selected");
          return;
        }

        isUploadingRef.current = true;
        setUploadingImage(true);

        try {
          // Convert base64 to blob
          const base64Data = updatedImage.src;
          const blob = await fetch(base64Data).then((res) => res.blob());

          // Create file from blob with unique timestamp
          const timestamp = Date.now();
          const file = new File([blob], `edited-image-${timestamp}.png`, {
            type: "image/png",
          });

          // Upload to server (only happens when user clicks Save in image editor)
          const serverUrl = await uploadImage(file);

          if (serverUrl) {
            // Update with server URL instead of base64
            selectedImage.src = serverUrl;
            // Remove any previous transforms since image is already transformed
            selectedImage.style.transform = "";
            selectedImage.style.backgroundColor = "";

            // Close image editor after successful upload
            setImageEditorOpen(false);
            selectImage(null);
          } else {
            alert("Failed to upload edited image. Please try again.");
          }
        } catch (error) {
          console.error("Error uploading edited image:", error);
          alert("Error uploading edited image. Please try again.");
        } finally {
          setUploadingImage(false);
          isUploadingRef.current = false;
        }
      },
      [selectedImage, uploadImage, selectImage]
    );

    // Sync content when value prop changes (controlled component)
    useEffect(() => {
      if (value !== undefined && value !== content) {
        setContent(value);
      }
    }, [value, content, setContent]);

    // Keyboard shortcuts
    useEffect(() => {
      const handleKeyDown = (event) => {
        if (event.ctrlKey || event.metaKey) {
          if (event.key === "z" && !event.shiftKey) {
            event.preventDefault();
            undo();
          } else if (
            (event.key === "z" && event.shiftKey) ||
            event.key === "y"
          ) {
            event.preventDefault();
            redo();
          } else if (event.key === "s") {
            event.preventDefault();
            if (onSave) {
              onSave(content);
            }
          }
        }
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }, [undo, redo, onSave, content]);

    // Make images draggable and attach event listeners
    useEffect(() => {
      const editor = getEditor();
      if (!editor) return;

      const editorElement = editor.root;

      const makeImagesDraggable = () => {
        const images = editorElement.querySelectorAll("img");
        images.forEach((img) => {
          img.draggable = true;
          img.addEventListener("dragstart", handleImageDragStart);
          img.addEventListener("dragend", handleImageDragEnd);
        });
      };

      // Initial setup
      makeImagesDraggable();

      // Observer to handle dynamically added images
      const observer = new MutationObserver(() => {
        makeImagesDraggable();
      });

      observer.observe(editorElement, {
        childList: true,
        subtree: true,
      });

      return () => {
        observer.disconnect();
        const images = editorElement.querySelectorAll("img");
        images.forEach((img) => {
          img.removeEventListener("dragstart", handleImageDragStart);
          img.removeEventListener("dragend", handleImageDragEnd);
        });
      };
    }, [getEditor, handleImageDragStart, handleImageDragEnd]);

    // Quill modules configuration
    const modules = {
      toolbar: {
        container: "#toolbar",
      },
      clipboard: {
        matchVisual: false,
      },
    };

    const formats = [
      "header",
      "font",
      "size",
      "bold",
      "italic",
      "underline",
      "strike",
      "color",
      "background",
      "script",
      "blockquote",
      "code-block",
      "list",
      "bullet",
      "indent",
      "align",
      "direction",
      "link",
      "image",
      "video",
    ];

    // Only show context menu for images
    const menuItems = imageContextMenuItems;

    return (
      <div
        className={`html-editor-container ${theme} ${className} ${
          isFullScreen ? "fullscreen" : ""
        }`}
      >
        <CustomToolbar
          editorRef={quillRef}
          onToggleSource={handleToggleSource}
          onToggleFullScreen={handleToggleFullScreen}
          isFullScreen={isFullScreen}
        />

        {showSource ? (
          /* Source View */
          <div className="source-view-wrapper" style={{ height }}>
            <textarea
              className="source-view-textarea"
              value={sourceHTML}
              onChange={handleSourceChange}
              placeholder="Edit HTML source..."
              spellCheck={false}
            />
          </div>
        ) : (
          /* Visual Editor */
          <div
            className={`editor-wrapper ${isDragging ? "dragging" : ""}`}
            onContextMenu={handleContextMenu}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleImageClick}
            style={{ height }}
          >
            <ReactQuill
              ref={quillRef}
              value={content}
              onChange={handleChange}
              modules={modules}
              formats={formats}
              placeholder={placeholder}
              readOnly={readOnly}
              theme="snow"
              className="html-editor"
            />

            {isDragging && (
              <div className="drop-overlay">
                <div className="drop-message">📎 Drop files here</div>
              </div>
            )}

            {uploadingImage && (
              <div className="upload-overlay">
                <div className="upload-message">
                  <div className="spinner"></div>
                  Uploading image...
                </div>
              </div>
            )}
          </div>
        )}

        {/* Floating toolbar for images */}
        {floatingToolbar && (
          <ImageFloatingToolbar
            position={floatingToolbar}
            onAlign={handleImageAlign}
            onResize={handleImageResize}
            onEdit={handleImageEdit}
            onDelete={handleImageDelete}
            onClose={closeFloatingToolbar}
          />
        )}

        {contextMenu && (
          <ContextMenu
            position={contextMenu}
            onClose={hideContextMenu}
            menuItems={menuItems}
            selectedElement={selectedElement}
          />
        )}

        {imageEditorOpen && (
          <ImageEditor
            image={selectedImage}
            initialDimensions={imageProperties}
            onUpdate={handleImageUpdate}
            onClose={() => {
              setImageEditorOpen(false);
              selectImage(null);
            }}
          />
        )}
      </div>
    );
  }
);

HTMLEditor.displayName = "HTMLEditor";

export default HTMLEditor;
